import {
  CurrentUser,
  ImpersonationToken,
  User,
  UserCurrency,
} from '@entities/user';
import {axi, axiPayment, GetListOutput, GetListParams} from '@shared/api';
import {currencyValueDivider} from '@shared/constants';
import {validateData} from '@shared/utils';
import {
  currentUserV8nSchema,
  impersonationTokenV8nSchema,
  userCurrencyV8nSchema,
  userV8nSchema,
  v8nSchemaOfGetUsersResponse,
} from './backendSchemas';
import {
  CurrentUserOnBackend,
  GetUsersResponse,
  ImpersonationTokenOnBackend,
  UserCurrencyOnBackend,
  UserOnBackend,
} from './backendTypes';
import {
  mapCurrentUserFromBackend,
  mapImpersonationTokenFromBackend,
  mapUserFromBackend,
} from './dataMappers';

export const getCurrentUser = async (): Promise<CurrentUser> => {
  const response = await axi.get<CurrentUserOnBackend>('/profile/current_user');

  validateData(response.data, currentUserV8nSchema, 'getCurrentUser');

  return mapCurrentUserFromBackend(response.data);
};

export type UserSortField = 'username' | 'registered_at';
export type SortOrder = 'asc' | 'desc';

export type GetUsersParams = GetListParams & {
  searchQuery?: string;
  sortField?: UserSortField;
  sortOrder?: SortOrder;
};

export const getUsers = async ({
  page,
  pageSize,
  searchQuery,
  sortField,
  sortOrder,
}: GetUsersParams = {}): Promise<GetListOutput<User>> => {
  const response = await axi.get<GetUsersResponse>('/admin/users', {
    params: {
      page,
      per_page: pageSize,
      search_query: searchQuery || undefined,
      sort_field: sortField,
      sort_order: sortOrder,
    },
  });

  const data = response.data;

  validateData(data, v8nSchemaOfGetUsersResponse, 'getUsers');

  const {
    total,
    total_pages,
    array,
    page: pageFromBackend,
    per_page: pageSizeFromBackend,
  } = data;

  return {
    page: pageFromBackend,
    pageSize: pageSizeFromBackend,
    total,
    totalPages: total_pages,
    items: array.map(item => mapUserFromBackend(item)),
  };
};

export const getUser = async (userId: string): Promise<User> => {
  const response = await axi.get<UserOnBackend>(`/admin/users/${userId}`);

  validateData(response.data, userV8nSchema, 'getUser');

  return mapUserFromBackend(response.data);
};

export interface SetAdminParams {
  userId: string;
  isAdmin: boolean;
}

export const setAdmin = ({userId, isAdmin}: SetAdminParams): Promise<void> => {
  return axi.put(
    `/admin/users/${userId}/${isAdmin ? 'grant_admin' : 'revoke_admin'}`,
  );
};

export interface TopUpRealParams {
  username: string;
  dollars: number;
}

export const topUpReal = ({
  username,
  dollars,
}: TopUpRealParams): Promise<void> => {
  return axiPayment.post(`/admin/users/${username}/real/top_up`, {
    amount: dollars * currencyValueDivider,
    ik: crypto.randomUUID(),
  });
};

export interface TopUpPromoParams {
  username: string;
  dollars: number;
}

export const topUpPromo = ({
  username,
  dollars,
}: TopUpPromoParams): Promise<void> => {
  return axiPayment.post(`/admin/users/${username}/promo/top_up`, {
    amount: dollars * currencyValueDivider,
    ik: crypto.randomUUID(),
  });
};

export interface TopUpLocalParams {
  username: string;
  amount: number;
}

const topUpLocal = (
  balance: 'real' | 'promo',
  {username, amount}: TopUpLocalParams,
): Promise<void> =>
  axiPayment.post(`/admin/users/${username}/${balance}/top_up_local`, {
    amount: amount * currencyValueDivider,
    ik: crypto.randomUUID(),
  });

export const topUpRealLocal = (params: TopUpLocalParams): Promise<void> =>
  topUpLocal('real', params);

export const topUpPromoLocal = (params: TopUpLocalParams): Promise<void> =>
  topUpLocal('promo', params);

export const getUserCurrency = async (
  username: string,
): Promise<UserCurrency> => {
  const response = await axiPayment.get<UserCurrencyOnBackend>(
    `/admin/users/${username}/currency`,
  );

  validateData(response.data, userCurrencyV8nSchema, 'getUserCurrency');

  return response.data;
};

export interface TopUpCoinsParams {
  username: string;
  coins: number;
}

export const topUpCoins = ({
  username,
  coins,
}: TopUpCoinsParams): Promise<void> => {
  return axiPayment.post(`/admin/users/${username}/coins/top_up`, {
    amount: coins,
    ik: crypto.randomUUID(),
  });
};

export interface TopUpCfpParams {
  username: string;
  amount: number;
}

export const topUpCfp = ({username, amount}: TopUpCfpParams): Promise<void> => {
  return axiPayment.post(`/admin/users/${username}/cfp/top_up`, {
    amount,
    ik: crypto.randomUUID(),
  });
};

export const issueImpersonationToken = async (
  userId: string,
): Promise<ImpersonationToken> => {
  const response = await axi.post<ImpersonationTokenOnBackend>(
    `/admin/users/${userId}/impersonation-token`,
  );

  validateData(
    response.data,
    impersonationTokenV8nSchema,
    'issueImpersonationToken',
  );

  return mapImpersonationTokenFromBackend(response.data);
};
